import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { GlobalEntityAutocomplete, EntityOption } from './GlobalEntityAutocomplete';
import { StandardOperatingProcedure } from '../types';

interface SOPAutoCompleteProps {
  projectId: string;
  label: string;
  placeholder?: string;
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  currentEntityId?: string;
  currentEntityType?: 'URS' | 'FS' | 'TEST' | 'RISK';
}

export const SOPAutoComplete: React.FC<SOPAutoCompleteProps> = ({
  projectId,
  label,
  placeholder,
  selectedIds,
  onChange,
  currentEntityId,
  currentEntityType
}) => {
  const [options, setOptions] = useState<EntityOption[]>([]);
  const [, setLoading] = useState(true);
  const [readOnlyIds, setReadOnlyIds] = useState<string[]>([]);

  useEffect(() => {
    const loadSOPs = async () => {
      if (!projectId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const col = collection(db, 'projects', projectId, 'sops');
        const snapshot = await getDocs(col);
        const loaded: EntityOption[] = snapshot.docs.map(doc => {
          const data = doc.data() as StandardOperatingProcedure;
          return {
            id: data.id || `SOP-${doc.id.substring(0, 8)}`,
            label: data.title || data.id || `SOP-${doc.id.substring(0, 8)}`,
            title: data.title
          };
        });
        setOptions(loaded);
      } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Error loading SOPs for autocomplete:', err);
      }
        setOptions([]);
      } finally {
        setLoading(false);
      }
    };

    loadSOPs();
  }, [projectId]);

  // Query for reverse relationships
  useEffect(() => {
    const queryReverseLinks = async () => {
      if (!projectId || !currentEntityId || !currentEntityType) {
        setReadOnlyIds([]);
        return;
      }

      try {
        const reverseIds: string[] = [];
        const sopCol = collection(db, 'projects', projectId, 'sops');
        const sopSnapshot = await getDocs(sopCol);
        sopSnapshot.docs.forEach(doc => {
          const data = doc.data();
          const links = data.links || {};
          
          if (currentEntityType === 'URS' && links.urs_ids?.includes(currentEntityId)) {
            const id = data.id || `SOP-${doc.id.substring(0, 8)}`;
            reverseIds.push(id);
          } else if (currentEntityType === 'FS' && links.fs_ids?.includes(currentEntityId)) {
            const id = data.id || `SOP-${doc.id.substring(0, 8)}`;
            reverseIds.push(id);
          } else if (currentEntityType === 'TEST' && links.test_ids?.includes(currentEntityId)) {
            const id = data.id || `SOP-${doc.id.substring(0, 8)}`;
            reverseIds.push(id);
          } else if (currentEntityType === 'RISK' && links.risk_ids?.includes(currentEntityId)) {
            const id = data.id || `SOP-${doc.id.substring(0, 8)}`;
            reverseIds.push(id);
          }
        });

        setReadOnlyIds(reverseIds);
      } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Error querying reverse links for SOP:', err);
      }
        setReadOnlyIds([]);
      }
    };

    queryReverseLinks();
  }, [projectId, currentEntityId, currentEntityType]);

  return (
    <GlobalEntityAutocomplete
      label={label}
      placeholder={placeholder}
      selectedIds={selectedIds}
      options={options}
      onChange={onChange}
      readOnlyIds={readOnlyIds}
    />
  );
};

